// Overview: a customizable dashboard. The kit's <Dashboard> owns the grid,
// the layout switcher and the Customize builder; this page only supplies the
// header copy, the data sources and built-in layouts (./sources.tsx), and the
// router-aware link.
//
// UI-WORDING RULE: headings use plain language. Technical terms live in muted
// helper text and tooltips only.

import { Dashboard } from "@sylviq/ui-react-web/components/dashboard/Dashboard";
import type {
  DashboardState,
  DashboardStore,
} from "@sylviq/ui-react-web/components/dashboard/model";
import type { DashboardLink } from "@sylviq/ui-react-web/components/dashboard/widgets";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearOverviewReadCache,
  createSources,
  defaultPresets,
  ensureCashForecastInLayout,
} from "@/features/overview/sources";
import { getDashboardLayouts, saveDashboardLayout } from "@/lib/api";
import { useOrganization } from "@/lib/organization";

// Module-level so its identity is stable: an inline component would remount
// every link on each render.
const RouterLink: DashboardLink = ({ href, className, children }) => (
  <Link to={href} className={className}>
    {children}
  </Link>
);

export function OverviewPage() {
  const { organizations, scope } = useOrganization();
  const org = organizations.find((o) => o.id === scope?.organization_id);
  const sources = useMemo(
    () =>
      createSources(
        scope?.organization_id ?? "-",
        scope?.legal_entity_id ?? "-",
      ),
    [scope?.organization_id, scope?.legal_entity_id],
  );
  const versions = useRef<{ personal?: number; organizationDefault?: number }>({});
  const [canSaveOrganizationDefault, setCanSaveOrganizationDefault] = useState(false);

  // Intentional reset-on-scope-change: the body clears stale layout
  // versions so a switched organization never writes against the previous
  // scope's version. Biome flags the optional-chain deps as removable, but
  // dropping them would stop the reset from firing on scope change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset must fire on scope change
  useEffect(() => {
    versions.current = {};
    setCanSaveOrganizationDefault(false);
  }, [scope?.organization_id]);

  const store = useMemo<DashboardStore | undefined>(() => {
    if (!scope) return undefined;
    return {
      async read() {
        const layouts = await getDashboardLayouts();
        versions.current = {
          personal: layouts.personal?.version,
          organizationDefault: layouts.workspace_default?.version,
        };
        setCanSaveOrganizationDefault(layouts.can_write_workspace_default);
        const saved =
          layouts.personal?.state ?? layouts.workspace_default?.state ?? null;
        return saved
          ? ensureCashForecastInLayout(saved as DashboardState)
          : null;
      },
      async write(state) {
        const saved = await saveDashboardLayout({
          target: "personal",
          state,
          version: versions.current.personal,
        });
        versions.current.personal = saved.version;
      },
    };
  }, [scope]);

  const saveOrganizationDefault = useCallback(async (state: DashboardState) => {
    const saved = await saveDashboardLayout({
      target: "workspace_default",
      state,
      version: versions.current.organizationDefault,
    });
    versions.current.organizationDefault = saved.version;
  }, []);

  return (
    <div
      onClickCapture={(event) => {
        // The dashboard kit owns its Refresh control; clear the short-lived
        // read cache before that control reloads every source.
        const target = event.target;
        if (
          target instanceof Element &&
          target.closest('button[aria-label="Refresh"]')
        ) {
          clearOverviewReadCache();
        }
      }}
    >
      <Dashboard
        // Layouts are remembered per organization, so switching organizations
        // never shows another books' arrangement.
        key={scope?.organization_id ?? "none"}
        sources={sources}
        defaults={defaultPresets}
        storageKey={`niche-accounting.overview.v1:${scope?.organization_id ?? "-"}`}
        store={store}
        onSaveWorkspaceDefault={
          canSaveOrganizationDefault ? saveOrganizationDefault : undefined
        }
        Link={RouterLink}
        renderHeader={(controls) => (
          <PageHeader
            title="Overview"
            description={
              org ? (
                <>
                  The money picture for{" "}
                  <span className="font-medium">{org.name}</span>.
                </>
              ) : (
                "The money picture for this organization."
              )
            }
            actions={controls}
          />
        )}
      />
    </div>
  );
}
